'use client';

import * as Popover from '@radix-ui/react-popover';
import clsx from 'clsx';

import { Button } from '@/components/Button/Button';
import { FAQsModal } from '@/components/Modal/FAQsModal/FAQsModal';
import { BASE_PATH } from '@/constants/constants';
import { type CMSData } from '@/data/cms-data';
import { analyticsEventHandlers } from '@/lib/analytics/events/generalAnalyticsEvents';

import './HelpWidget.scss';

export const HELP_WIDGET_ID = 'help-widget-button';

type HelpWidgetProps = Readonly<{ className?: string; helpWidgetData: CMSData['settings']['help-widget'] }>;

type ContactButtonProps = { children: React.ReactNode; href: string; icon: string; testId: string; onClick?: () => void };

const ContactButton = ({ children, href, icon, testId, onClick }: ContactButtonProps) => {
	return (
		<Button
			as="a"
			className="help-widget__contact-button"
			data-testid={testId}
			variant="tertiary"
			theme="dark"
			icon={icon}
			iconPosition="left"
			iconSize="1em"
			href={href}
			onClick={onClick}
		>
			{children}
		</Button>
	);
};

export function HelpWidget({ className, helpWidgetData: { button_label, title, faqs, lists } }: HelpWidgetProps) {
	return (
		<Popover.Root
			onOpenChange={(open) => {
				if (open) {
					analyticsEventHandlers.trackHelpWidgetOpened();
				} else {
					analyticsEventHandlers.trackHelpWidgetClosed();
				}
			}}
		>
			<Popover.Trigger asChild>
				<button
					type="button"
					className={clsx('[ help-widget__button ] [ m-0 p-0 border-radius-full color-fill-brand color-text-white ]', className)}
					data-testid="help-widget-button"
					id={HELP_WIDGET_ID}
					// This is to make sure that the button expands enough to fit the text for borwsers that don't support interpolate-size CSS property.
					style={{ '--help-widget-label-size': `${button_label.length + 4}ch` } as React.CSSProperties}
				>
					<span className=" ml-3 text-button-base">{button_label}</span>
					<div className="d-flex justify-center items-center p-3">
						<svg data-icon="close" width="1em" height="1em" fill="currentColor">
							<use xlinkHref={`${BASE_PATH}/speedboat/icons/toyota-icons.svg#close`} />
						</svg>
						<svg data-icon="question" width="1em" height="1em" fill="currentColor">
							<use xlinkHref={`${BASE_PATH}/speedboat/icons/toyota-icons.svg#question-circle`} />
						</svg>
					</div>
				</button>
			</Popover.Trigger>
			<Popover.Portal>
				<Popover.Content className="[ help-widget__popover ]" side="top">
					<div className="[ help-widget__popover-content ] [ d-flex d-flex-column gap-4 sm:gap-6 items-start mb-2 sm:mb-4 mx-2 sm:mx-4 p-4 sm:p-6 color-fill-brand color-text-white ]">
						<p className="text-heading-5 color-text-white">{title}</p>
						{faqs && (
							<div>
								<p className="mb-2">{faqs.list_label}</p>
								<FAQsModal buttonLabel={faqs.modal_button_label} modalTitle={faqs.modal_title} faqs={faqs.faqs_list} />
							</div>
						)}
						{lists.contact_us_list && lists.contact_us_list.list_items.length > 0 && (
							<div>
								<p className="mb-2 sm:mb-4">{lists.contact_us_list.list_label}</p>
								<div className="d-flex d-flex-column gap-2">
									<ContactButton
										href={`tel:${lists.contact_us_list.list_items[0]?.contact_label.replaceAll(' ', '')}`}
										icon="telephone"
										testId="help-widget__telephone-button"
										onClick={analyticsEventHandlers.trackHelpWidgetPhoneClicked}
									>
										{lists.contact_us_list.list_items[0]?.contact_label}
									</ContactButton>
									{lists.contact_us_list.list_items[1]?.contact_label && (
										<ContactButton
											href={`mailto:${lists.contact_us_list.list_items[1].contact_label}`}
											icon="envelope"
											testId="help-widget__email-button"
											onClick={analyticsEventHandlers.trackHelpWidgetEmailClicked}
										>
											{lists.contact_us_list.list_items[1].contact_label}
										</ContactButton>
									)}
								</div>
							</div>
						)}
						{/* this is only temporary until the List Component is ready to be built, needed to add opening hours integration to the CMS anyway */}
						<table className="table__space-between w-full text-body-sm">
							<thead>
								<tr>
									<th className="text-caption-1 p-0" colSpan={2}>
										<p className="mb-2">{lists.opening_hours_list.list_label}</p>
									</th>
								</tr>
							</thead>
							<tbody>
								{lists.opening_hours_list.list_items.map((hoursItem) => {
									return (
										<tr key={hoursItem.day_label} className="text-subheading-sm">
											<td className="mb-2 p-0">{hoursItem.day_label}</td>
											<td className="mb-2 p-0">{hoursItem.hours_value}</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				</Popover.Content>
			</Popover.Portal>
		</Popover.Root>
	);
}
